<template>
  <NtPageHeader
    v-if="depth === 0"
    :heading="$route.matched[depth].meta.name"
    :introduction="$route.matched[depth].meta.introduction" />

  <NtTabContainer v-if="siblingTabs.length > 1" :tabs="siblingTabs" :selected="selectedSiblingTab" />

  <NtLoader :loading="!page" debounce>
    <NtSidebarLayout v-if="page.modules.length > 0">
      <template v-for="m in pageModules" :key="m.id">
        <NtAlert v-if="m.permission && $cannot(m.permission)" level="danger" heading="Access Denied">
          You do not have permission to {{ m.permission }}.
        </NtAlert>
        <component v-else :is="m.component">
          <NtMissingModule :component="m.component" />
        </component>
      </template>

      <template v-if="sidebarModules.length > 0" v-slot:sidebar>
        <template v-for="m in sidebarModules" :key="m.id">
          <NtAlert v-if="m.permission && $cannot(m.permission)" level="danger" heading="Access Denied">
            You do not have permission to {{ m.permission }}.
          </NtAlert>
          <component v-else :is="m.component">
            <NtMissingModule :component="m.component" />
          </component>
        </template>
      </template>
    </NtSidebarLayout>

    <div v-if="page.show_tabs" :class="{ 'mt-6': page.modules.length > 0 }">
      <NtTabContainer :tabs="tabs" :selected="selectedTab" />
      <RouterView :depth="depth + 1" />
    </div>
  </NtLoader>
</template>

<script>
import { mapState } from 'pinia'
import { useSystemStore } from '@/stores/system'
import NtSidebarLayout from '@/layouts/NtSidebarLayout.vue'
import NtPageHeader from '@/components/general/NtPageHeader.vue'
import NtLoader from '@/components/general/NtLoader.vue'
import NtTabContainer from '@/components/navigation/NtTabContainer.vue'
import { RouterView } from 'vue-router'
import NtAlert from '@/components/general/NtAlert.vue'
import NtButton from '@/components/general/NtButton.vue'
import NtMissingModule from '@/components/general/NtMissingModule.vue'
import NtModal from '@/components/general/NtModal.vue'
import NtSpacing from '@/components/general/NtSpacing.vue'
import { useAuthStore } from '@/stores/auth'

export default {
  components: {
    NtSidebarLayout,
    NtPageHeader,
    NtLoader,
    NtTabContainer,
    RouterView,
    NtAlert,
    NtButton,
    NtMissingModule,
    NtModal,
    NtSpacing
  },
  props: {
    depth: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      page: null
    }
  },
  computed: {
    ...mapState(useSystemStore, ['hub', 'routes', 'confirmExitMethods']),
    ...mapState(useAuthStore, ['authUser']),
    pageModules() {
      return this.page?.modules.filter((m) => !m.sidebar) || []
    },
    sidebarModules() {
      return this.page?.modules.filter((m) => m.sidebar) || []
    },
    selectedTab() {
      return this.page?.show_tabs && this.$route.matched.length > this.depth + 1
        ? this.$route.matched[this.depth + 1].meta.id
        : null
    },
    selectedSiblingTab() {
      return this.$route.matched[this.depth].meta.id
    },
    tabs() {
      if (!this.page?.show_tabs) {
        return null
      }
      return this.routes.filter(
        (r) =>
          r.parent_id === this.$route.matched[this.depth].meta.id &&
          !r.modules.find((m) => m.model !== null) &&
          (!r.permission || (r.permission && this.authUser.admin_access_permissions_list.includes(r.permission)))
      )
    },
    siblingTabs() {
      const model = this.page.modules.find((m) => m.model)?.model
      if (!model) {
        return []
      }
      return this.routes.filter(
        (r) =>
          r.parent_id === this.$route.matched[this.depth].meta.parent_id && r.modules.find((m) => m.model === model)
      )
    }
  },
  watch: {
    $route: {
      handler(newVal, oldVal) {
        if (newVal.matched[this.depth].meta.id === oldVal?.matched[this.depth].meta.id) {
          return
        }
        this.page = newVal.matched[this.depth].meta
        if (this.page.show_tabs && this.$route.matched.length - 1 === this.depth && this.tabs.length > 0) {
          this.$router.replace({ name: `page.${this.tabs[0].id}`, query: this.$route.query })
        }
      },
      immediate: true
    }
  }
}
</script>
